import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useScreensaver } from '../../context/ScreensaverContext';
import useIdleDetection from '../../hooks/useIdleDetection';
import { getTheme, SHUFFLE_KEY, pickRandomThemeKey } from './screensaver/themes';
import { getClock } from './screensaver/clocks';
import ThemeErrorBoundary from './screensaver/ThemeErrorBoundary';

function IdleScreensaver() {
  const { settings, manualActive, manualDismiss, palette, backgroundStyles } = useScreensaver();
  const { theme } = useTheme();
  const isIdle = useIdleDetection(settings.timeoutMs);

  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const [acceptDismiss, setAcceptDismiss] = useState(false);
  const [time, setTime] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [gsapReady, setGsapReady] = useState(false);
  const [shuffleKey, setShuffleKey] = useState(null);
  const anchorRef = useRef(null); // mouse position when screensaver became dismissible
  const DISMISS_MOVE_THRESHOLD = 40; // px

  // GSAP entrance/exit refs
  const gsapRef = useRef(null);
  const rootRef = useRef(null);
  const wordmarkRef = useRef(null);
  const clockWrapRef = useRef(null);
  const weatherRef = useRef(null);
  const hintRef = useRef(null);
  const breathTweenRef = useRef(null);

  const active = isIdle || manualActive;
  const dark = theme === 'dark';

  // Load gsap lazily, only once the screensaver first activates. The screensaver
  // works without it (CSS fade fallback), so we don't pay the chunk cost on every
  // dashboard load. The entrance effect re-runs on gsapReady once it lands.
  useEffect(() => {
    if (!active || gsapRef.current) return undefined;
    let cancelled = false;
    import('gsap')
      .then((mod) => {
        if (cancelled) return;
        gsapRef.current = mod.gsap || mod.default;
        setGsapReady(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [active]);

  useEffect(() => {
    if (active) {
      if (settings.theme === SHUFFLE_KEY) setShuffleKey(pickRandomThemeKey());
      setVisible(true);
      setFading(false);
      setAcceptDismiss(false);
      anchorRef.current = null;
      const t = setTimeout(() => setAcceptDismiss(true), 1200);
      return () => clearTimeout(t);
    } else if (visible) {
      const gsap = gsapRef.current;
      if (gsap && rootRef.current) {
        if (breathTweenRef.current) {
          breathTweenRef.current.kill();
          breathTweenRef.current = null;
        }
        rootRef.current.style.pointerEvents = 'none';
        gsap.to(rootRef.current, {
          autoAlpha: 0,
          duration: 0.4,
          ease: 'power2.in',
          onComplete: () => setVisible(false),
        });
        return undefined;
      }
      setFading(true);
      const t = setTimeout(() => { setVisible(false); setFading(false); }, 600);
      return () => clearTimeout(t);
    }
  }, [active]);

  // GSAP entrance: backdrop fade, then wordmark -> clock -> weather -> hint stagger in.
  useEffect(() => {
    if (!visible) return undefined;
    const gsap = gsapRef.current;
    if (!gsap || !rootRef.current) return undefined;

    const reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let tl = null;

    if (reduced) {
      gsap.fromTo(rootRef.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4, ease: 'power1.out' });
    } else {
      const targets = [wordmarkRef.current, clockWrapRef.current, weatherRef.current, hintRef.current]
        .filter(Boolean);
      tl = gsap.timeline();
      tl.fromTo(rootRef.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, ease: 'power1.out' });
      tl.fromTo(
        targets,
        { autoAlpha: 0, y: 14 },
        { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.12, ease: 'power2.out' },
        '-=0.25'
      );
      if (clockWrapRef.current) {
        breathTweenRef.current = gsap.to(clockWrapRef.current, {
          scale: 1.01,
          duration: 4,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut',
          delay: 1,
        });
      }
    }

    return () => {
      if (tl) tl.kill();
      if (breathTweenRef.current) {
        breathTweenRef.current.kill();
        breathTweenRef.current = null;
      }
    };
  }, [visible, gsapReady]);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => setTime(new Date()), settings.clockStyle === 'flip' ? 1000 : 500);
    return () => clearInterval(interval);
  }, [visible, settings.clockStyle]);

  useEffect(() => {
    if (!visible || weather || !settings.showWeather) return;
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code,is_day&daily=sunrise,sunset&timezone=auto&temperature_unit=fahrenheit`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.current) {
              setWeather(prev => ({
                ...prev,
                temp: Math.round(data.current.temperature_2m),
                code: data.current.weather_code,
                isDay: data.current.is_day === 1,
                sunrise: data.daily?.sunrise?.[0] || null,
                sunset: data.daily?.sunset?.[0] || null,
              }));
            }
          })
          .catch(() => {});
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=10`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.address) {
              const city = data.address.city || data.address.town || data.address.village || data.address.county || '';
              const state = data.address.state || '';
              if (city) {
                setWeather(prev => ({ ...prev, city: state ? `${city}, ${state}` : city }));
              }
            }
          })
          .catch(() => {});
      },
      () => {}
    );
  }, [visible, settings.showWeather]);

  const handleMouseMove = (e) => {
    if (!acceptDismiss) return;
    if (!anchorRef.current) {
      anchorRef.current = { x: e.clientX, y: e.clientY };
      return;
    }
    const dx = e.clientX - anchorRef.current.x;
    const dy = e.clientY - anchorRef.current.y;
    if (Math.hypot(dx, dy) >= DISMISS_MOVE_THRESHOLD && manualActive) {
      manualDismiss();
    }
  };

  const handleClick = () => {
    if (!acceptDismiss) return;
    if (manualActive) manualDismiss();
  };

  const handleKeyDown = () => {
    if (manualActive) manualDismiss();
  };

  useEffect(() => {
    if (!visible) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, manualActive]);

  if (!visible) return null;

  const effectiveThemeKey = settings.theme === SHUFFLE_KEY ? (shuffleKey || 'particles') : settings.theme;
  const ThemeComponent = getTheme(effectiveThemeKey).component;
  const ClockComponent = getClock(settings.clockStyle).component;

  const period = weather ? getPeriod(weather, time) : 'day';
  const weatherIcon = weather ? getWeatherIcon(weather.code, period) : null;

  // Background resolution
  const bgStyle = (() => {
    if (settings.background === 'auto') {
      return { background: dark ? '#030712' : '#f9fafb' };
    }
    return backgroundStyles[settings.background] || { background: dark ? '#030712' : '#f9fafb' };
  })();

  // When gsap drives opacity, the CSS transition must not fight it.
  const fadeClasses = gsapReady
    ? ''
    : `transition-opacity duration-500 ${fading ? 'opacity-0' : 'opacity-100'}`;

  return (
    <div
      ref={rootRef}
      className={`fixed inset-0 z-[60] ${fadeClasses}`}
      style={{ pointerEvents: fading ? 'none' : 'auto' }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      {/* Background */}
      <div className="absolute inset-0" style={bgStyle} />

      {/* Theme animation */}
      <ThemeErrorBoundary key={effectiveThemeKey}>
        <React.Suspense fallback={null}>
          <ThemeComponent palette={palette} colorMode={settings.colorMode} dark={dark} />
        </React.Suspense>
      </ThemeErrorBoundary>

      {/* Clock + Info overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center select-none pointer-events-none">
        <div ref={wordmarkRef} className={`text-3xl md:text-4xl font-dm-serif tracking-[0.3em] mb-6 ${dark ? 'text-white/30' : 'text-gray-500/50'}`}>
          JPRO
        </div>
        <div ref={clockWrapRef}>
          <ClockComponent time={time} settings={settings} dark={dark} palette={palette} />
        </div>

        {settings.showWeather && weather && (
          <div ref={weatherRef} className={`flex items-center gap-2 mt-6 text-lg font-rasa ${dark ? 'text-white/40' : 'text-gray-500/60'}`}>
            {weatherIcon && <span className="text-2xl">{weatherIcon}</span>}
            <span>{weather.temp}&deg;F</span>
            {weather.city && <span className="text-base">&middot; {weather.city}</span>}
          </div>
        )}

        <div ref={hintRef} className={`mt-12 text-xs font-rasa ${dark ? 'text-white/15' : 'text-gray-400/40'}`}>
          {manualActive ? 'Click, press a key, or move the mouse to dismiss' : 'Move your mouse to dismiss'}
        </div>
      </div>
    </div>
  );
}

// Determine period-of-day: 'sunrise' (±30m of sunrise), 'day', 'sunset' (±30m of sunset), 'night'
function getPeriod(weather, now) {
  if (!weather?.sunrise || !weather?.sunset) {
    return weather?.isDay === false ? 'night' : 'day';
  }
  const t = now.getTime();
  const sr = new Date(weather.sunrise).getTime();
  const ss = new Date(weather.sunset).getTime();
  const window = 30 * 60 * 1000; // 30 min twilight window
  if (Math.abs(t - sr) <= window) return 'sunrise';
  if (Math.abs(t - ss) <= window) return 'sunset';
  return t > sr && t < ss ? 'day' : 'night';
}

function getWeatherIcon(code, period = 'day') {
  // Clear sky: sunrise / sun / sunset / moon
  if (code === 0) {
    if (period === 'sunrise') return '\uD83C\uDF05'; // 🌅 sunrise
    if (period === 'sunset')  return '\uD83C\uDF07'; // 🌇 sunset (city)
    if (period === 'night')   return '\uD83C\uDF19'; // 🌙
    return '\u2600\uFE0F';                            // ☀️ day
  }
  // Partly cloudy
  if (code <= 3) {
    if (period === 'sunrise' || period === 'sunset') return '\uD83C\uDF24\uFE0F'; // 🌤
    if (period === 'night')                          return '\u2601\uFE0F';        // ☁️
    return '\u26C5';                                                                // ⛅ day
  }
  // Cloudy / fog
  if (code <= 48) return '\u2601\uFE0F';
  // Drizzle, rain, rain showers
  if ((code <= 57) || (code <= 67) || (code >= 80 && code <= 82)) {
    if (period === 'sunrise' || period === 'sunset') return '\uD83C\uDF26\uFE0F'; // 🌦 sun behind rain cloud
    if (period === 'night')                          return '\uD83C\uDF27\uFE0F'; // 🌧 cloud with rain
    return '\uD83C\uDF26\uFE0F';                                                   // 🌦 day
  }
  // Snow, snow showers
  if ((code <= 77) || (code >= 85 && code <= 86)) {
    if (period === 'night') return '\u2744\uFE0F';       // ❄️ snowflake at night
    return '\uD83C\uDF28\uFE0F';                          // 🌨 cloud with snow day/twilight
  }
  // Thunderstorm
  if (code >= 95) {
    if (period === 'night') return '\uD83C\uDF29\uFE0F'; // 🌩 cloud with lightning (darker)
    return '\u26C8\uFE0F';                                // ⛈ cloud with lightning and rain
  }
  return '\u2601\uFE0F';
}

export default IdleScreensaver;
